import type {
  CreateWhatsAppLocalTemplateRequest,
  WhatsAppTemplate,
} from '@workspace/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { useCallback, useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { whatsappAdminApi } from '../api/whatsappApi';

type WhatsAppLocalTemplatesSectionProps = {
  channelId: string;
  onChange?: () => void;
};

type EditorState = {
  name: string;
  bodyText: string;
  language: string;
  status: 'DRAFT' | 'ACTIVE';
};

const emptyEditor: EditorState = {
  name: '',
  bodyText: '',
  language: 'es',
  status: 'DRAFT',
};

export function WhatsAppLocalTemplatesSection({
  channelId,
  onChange,
}: WhatsAppLocalTemplatesSectionProps) {
  const [templates, setTemplates] = useState<WhatsAppTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editor, setEditor] = useState<EditorState>(emptyEditor);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const all = await whatsappAdminApi.listTemplates(channelId);
      setTemplates(all.filter(item => item.origin === 'LOCAL'));
    } catch (error) {
      toast.error((error as Error).message || 'No se pudieron cargar las plantillas');
    } finally {
      setLoading(false);
    }
  }, [channelId]);

  useEffect(() => {
    void load();
  }, [load]);

  const resetEditor = () => {
    setEditingId(null);
    setEditor(emptyEditor);
  };

  const startEdit = (template: WhatsAppTemplate) => {
    setEditingId(template.id);
    setEditor({
      name: template.name,
      bodyText: template.bodyText ?? '',
      language: template.language,
      status: template.status === 'ACTIVE' ? 'ACTIVE' : 'DRAFT',
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload: CreateWhatsAppLocalTemplateRequest = {
        name: editor.name,
        bodyText: editor.bodyText,
        language: editor.language,
        status: editor.status,
      };
      if (editingId) {
        const updated = await whatsappAdminApi.updateLocalTemplate(channelId, editingId, payload);
        setTemplates(prev => prev.map(item => (item.id === updated.id ? updated : item)));
        toast.success('Plantilla actualizada');
      } else {
        const created = await whatsappAdminApi.createLocalTemplate(channelId, payload);
        setTemplates(prev => [...prev, created]);
        toast.success('Plantilla creada');
      }
      resetEditor();
      onChange?.();
    } catch (error) {
      toast.error((error as Error).message || 'No se pudo guardar la plantilla');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (template: WhatsAppTemplate) => {
    const confirmed = window.confirm(
      template.status === 'ACTIVE'
        ? 'Esta plantilla activa puede estar en uso. ¿Confirmás borrarla?'
        : '¿Borrar esta plantilla del canal?',
    );
    if (!confirmed) return;
    try {
      await whatsappAdminApi.deleteLocalTemplate(channelId, template.id);
      setTemplates(prev => prev.filter(item => item.id !== template.id));
      if (editingId === template.id) {
        resetEditor();
      }
      toast.success('Plantilla eliminada');
      onChange?.();
    } catch (error) {
      toast.error((error as Error).message || 'No se pudo eliminar la plantilla');
    }
  };

  if (loading) {
    return <p className='text-sm text-muted-foreground'>Cargando plantillas del canal...</p>;
  }

  return (
    <div className='space-y-4'>
      <div className='space-y-2'>
        <p className='text-sm font-medium'>Plantillas del canal</p>
        <p className='text-xs text-muted-foreground'>
          Texto con variables como {'{{contact.firstName}}'} o {'{{debt.balance}}'}. Solo para este
          canal WAHA.
        </p>
      </div>

      <div className='grid gap-3 md:grid-cols-2'>
        <div>
          <Label>Nombre</Label>
          <Input
            value={editor.name}
            maxLength={255}
            onChange={event => setEditor({ ...editor, name: event.target.value })}
            placeholder='recordatorio_saldo'
          />
        </div>
        <div>
          <Label>Idioma</Label>
          <Input
            value={editor.language}
            maxLength={16}
            onChange={event => setEditor({ ...editor, language: event.target.value })}
          />
        </div>
      </div>
      <div>
        <Label>Texto</Label>
        <textarea
          className='flex min-h-[120px] w-full rounded-md border border-input bg-background px-3 py-2 text-sm'
          value={editor.bodyText}
          maxLength={4096}
          onChange={event => setEditor({ ...editor, bodyText: event.target.value })}
          placeholder='Hola {{contact.firstName}}, tu saldo es {{debt.balance}}'
        />
      </div>
      <div className='flex flex-wrap items-center gap-2'>
        <select
          className='flex h-10 rounded-md border border-input bg-background px-3 text-sm'
          value={editor.status}
          onChange={event =>
            setEditor({ ...editor, status: event.target.value as EditorState['status'] })
          }
        >
          <option value='DRAFT'>Borrador</option>
          <option value='ACTIVE'>Activa</option>
        </select>
        <Button type='button' onClick={handleSave} disabled={saving}>
          {editingId ? 'Guardar plantilla' : 'Crear plantilla'}
        </Button>
        {editingId ? (
          <Button type='button' variant='outline' onClick={resetEditor}>
            Cancelar edición
          </Button>
        ) : null}
      </div>

      <div className='space-y-2 max-h-64 overflow-y-auto'>
        {templates.length === 0 ? (
          <p className='text-sm text-muted-foreground'>Sin plantillas locales. Creá la primera arriba.</p>
        ) : (
          templates.map(template => (
            <div key={template.id} className='border rounded-lg p-3 text-sm space-y-1'>
              <div className='flex flex-wrap items-center gap-2'>
                <span className='font-medium'>{template.name}</span>
                <Badge variant='secondary'>{template.status}</Badge>
                <span className='text-muted-foreground'>{template.language}</span>
              </div>
              <p className='text-muted-foreground line-clamp-2'>{template.bodyText}</p>
              <div className='flex gap-2 pt-1'>
                <Button type='button' size='sm' variant='outline' onClick={() => startEdit(template)}>
                  Editar
                </Button>
                <Button
                  type='button'
                  size='sm'
                  variant='outline'
                  onClick={() => handleDelete(template)}
                >
                  Borrar
                </Button>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
}
